import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import DataTable from "../../../components/DataTable";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import {
  becomeCopyTradeMaster,
  getCopyTradeMasters,
  getCopyTradeMasterStatus,
  getCopyTradeMySubscriptions,
  getCopyTradeSymbolQuotes,
  getTradingAccounts,
  subscribeCopyTradeMaster,
  unsubscribeCopyTradeMaster,
  updateCopyTradeMaster,
} from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { isBecomeMasterDisabled, isCopyTradeDisabled } from "../../../utils/permissionHelper";
import { AccountType } from "../../../utils/enums";
import { useToast } from "../../../contexts/ToastContext";
import { attachCopyTradeQuotesPusher } from "../../../services/copyTradeQuotesPusher";
import "../../css/List.css";
import "../../css/CopyTrade.css";

const FILTER_TOP_ROI = "top_roi";
const FILTER_TOP_EQUITY = "top_equity";
const FILTER_LOW_RISK = "low_risk";
const PER_PAGE = 5;
/** Slow HTTP fallback only when WebSocket is down (protects API/MT5). */
const SYMBOL_QUOTES_FALLBACK_POLL_MS = 10000;

const COMMISSION_OPTIONS = [0, ...Array.from({ length: 19 }, (_, i) => (i + 1) * 5)]; // 0, 5..95
const RATIO_OPTIONS = [25, 50, 75, 100, 125, 150, 200];
const LOSS_LIMIT_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90];

const AVATAR_COLORS = [
  "#3A3A3A",
  "#2A3A4A",
  "#6B5B95",
  "#1A1A1A",
  "#4A5568",
  "#2D4A3E",
  "#5A3A2A",
  "#3A4A5A",
  "#5A4A1A",
  "#4A2A5A",
];

const formatEquity = (value) =>
  Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatGrowth = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (n > 0) return `+${abs}%`;
  if (n < 0) return `-${abs}%`;
  return `${abs}%`;
};

const growthClass = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "copy-trade-growth";
  return n > 0 ? "copy-trade-growth is-up" : "copy-trade-growth is-down";
};

const formatSymbolPrice = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const precision = Math.max(0, Math.min(8, Number(digits) || 2));
  return n.toLocaleString("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

const mapQuoteFromApi = (row) => {
  const key = String(row?.KEY ?? row?.key ?? row?.SYMBOL ?? row?.symbol ?? "")
    .trim()
    .toUpperCase();
  const symbol = String(row?.SYMBOL ?? row?.symbol ?? key).trim().toUpperCase();
  if (!key && !symbol) return null;
  const digits = Number(row?.DIGITS ?? row?.digits ?? 2);
  const mid = Number(row?.MID ?? row?.mid ?? row?.BID ?? row?.bid ?? 0);
  const change = Number(row?.CHANGE ?? row?.change ?? 0);
  return {
    symbol: key || symbol,
    display: String(row?.DISPLAY ?? row?.display ?? symbol).trim() || symbol,
    price: formatSymbolPrice(mid, digits),
    change: Number.isFinite(change) ? change : 0,
  };
};

const extractQuotesPayload = (response) => {
  const data = response?.decryptedData || response?.data || {};
  const payload = data?.DATA ?? data?.data ?? data?.BODY?.DATA ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const formatCopiers = (value) => Number(value).toLocaleString("en-US");

const pickAvatarColor = (seed) => {
  const text = String(seed || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const mapMasterFromApi = (row) => {
  const name = String(row?.NAME ?? row?.name ?? "").trim();
  const tradingLogin = String(row?.LOGIN ?? row?.tradingLogin ?? "").trim();
  return {
    id: Number(row?.ID ?? row?.id ?? 0),
    name,
    initials: (name.charAt(0) || "?").toUpperCase(),
    avatarColor: pickAvatarColor(name || tradingLogin),
    verified: false,
    api: false,
    copiers: Number(row?.COPIERS ?? row?.copiers ?? 0),
    tradingLogin,
    equity: Number(row?.EQUITY ?? row?.equity ?? 0),
    weeklyGrowth: Number(row?.WEEKLY_GROWTH_PERCENT ?? row?.weeklyGrowthPercent ?? 0),
    totalGrowth: Number(row?.TOTAL_GROWTH_PERCENT ?? row?.totalGrowthPercent ?? 0),
    commission: Number(row?.COMMISSION_PERCENT ?? row?.commission ?? 0),
    slotsFull: Boolean(row?.SLOTS_FULL ?? row?.slotsFull ?? false),
  };
};

const extractMastersPayload = (response) => {
  const data = response?.decryptedData || response?.data || {};
  const payload = data?.DATA ?? data?.data ?? data?.BODY?.DATA ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const extractAccountsPayload = (response) => {
  const data = response?.decryptedData || response?.data || {};
  const payload = data?.DATA ?? data?.data ?? data?.BODY?.DATA ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getAccountTypeValue = (account) => {
  const raw =
    account?.TYPE ??
    account?.type ??
    account?.ACCOUNT_TYPE ??
    account?.accountType ??
    account?.account?.TYPE ??
    account?.account?.type;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const mapAccountOption = (account) => {
  const accountInfo =
    account?.account_information ||
    account?.accountInformation ||
    account?.ACCOUNT_INFORMATION ||
    {};
  // LOGIN must be the MT trading id, not the CRM ACCOUNT_ID.
  const login = String(
    accountInfo?.TRADING_ID ??
      accountInfo?.trading_id ??
      account?.TRADING_ID ??
      account?.trading_id ??
      ""
  ).trim();
  if (!login || !/^\d+$/.test(login)) return null;

  const name = String(
    accountInfo?.NAME ??
      accountInfo?.name ??
      account?.NICK ??
      account?.NICK_NAME ??
      account?.NAME ??
      ""
  ).trim();
  const type = getAccountTypeValue(account);
  const typeLabel =
    type === AccountType.BROKER
      ? "Broker"
      : type === AccountType.REAL
        ? "Real"
        : "";
  const baseLabel = name ? `${name} (${login})` : login;

  return {
    login,
    label: typeLabel ? `${baseLabel} — ${typeLabel}` : baseLabel,
    type,
  };
};

const mergeAccountOptions = (...lists) => {
  const byLogin = new Map();
  lists.flat().forEach((option) => {
    if (!option?.login) return;
    if (!byLogin.has(option.login)) {
      byLogin.set(option.login, option);
    }
  });
  return Array.from(byLogin.values());
};

export function BecomeMasterModal({
  isOpen,
  onClose,
  onSuccess,
  t,
  mode = "create",
  initialValues = null,
}) {
  const isEdit = mode === "edit";
  const { success: showSuccess, error: showError } = useToast();
  const [fullName, setFullName] = useState("");
  const [commission, setCommission] = useState("10");
  const [accountNumber, setAccountNumber] = useState("");
  const [commissionAccount, setCommissionAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setFullName("");
      setCommission("10");
      setAccountNumber("");
      setCommissionAccount("");
      setAccounts([]);
      setAccountsLoading(false);
      setSubmitting(false);
      setFormError("");
      return undefined;
    }

    if (isEdit && initialValues) {
      setFullName(String(initialValues.name || "").trim());
      setCommission(String(initialValues.commission ?? 10));
      setAccountNumber(String(initialValues.login || "").trim());
      setCommissionAccount(String(initialValues.commissionLogin || "").trim());
    }

    let cancelled = false;
    const loadAccounts = async () => {
      setAccountsLoading(true);
      try {
        const [realResponse, brokerResponse] = await Promise.all([
          getTradingAccounts(AccountType.REAL, 100, 1, null, {
            forceRefresh: true,
          }),
          getTradingAccounts(AccountType.BROKER, 100, 1, null, {
            forceRefresh: true,
          }),
        ]);
        if (cancelled) return;

        const options = mergeAccountOptions(
          extractAccountsPayload(realResponse)
            .map(mapAccountOption)
            .filter(
              (option) =>
                option &&
                (option.type === AccountType.REAL ||
                  option.type === AccountType.BROKER)
            ),
          extractAccountsPayload(brokerResponse)
            .map(mapAccountOption)
            .filter(
              (option) =>
                option &&
                (option.type === AccountType.REAL ||
                  option.type === AccountType.BROKER)
            )
        );

        setAccounts(options);

        if (!isEdit) {
          if (options.length === 1) {
            setAccountNumber(options[0].login);
            setCommissionAccount(options[0].login);
          }
        } else {
          setAccountNumber((current) => {
            if (current && options.some((option) => option.login === current)) {
              return current;
            }
            return options.length === 1 ? options[0].login : "";
          });
          setCommissionAccount((current) => {
            if (current && options.some((option) => option.login === current)) {
              return current;
            }
            return options.length === 1 ? options[0].login : "";
          });
        }
      } catch (_) {
        if (!cancelled) {
          setAccounts([]);
          setFormError(
            t("copy_trade_accounts_load_error", {
              ns: "mobile",
              defaultValue: "Unable to load your trading accounts.",
            })
          );
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    };
    loadAccounts();

    return () => {
      cancelled = true;
    };
    // Prefill only when the dialog opens; avoid resetting while the user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, t, isEdit]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const name = fullName.trim();
    const loginRaw = String(accountNumber || "").trim();
    const commissionLoginRaw = String(commissionAccount || "").trim();
    const commissionPercent = Number(commission);

    if (!isEdit && !name) {
      setFormError(
        t("copy_trade_name_required", {
          ns: "mobile",
          defaultValue: "Full name is required.",
        })
      );
      return;
    }
    if (!loginRaw || !/^\d+$/.test(loginRaw)) {
      setFormError(
        t("copy_trade_login_required", {
          ns: "mobile",
          defaultValue: "Select a trading account.",
        })
      );
      return;
    }
    if (!COMMISSION_OPTIONS.includes(commissionPercent)) {
      setFormError(
        t("copy_trade_commission_invalid", {
          ns: "mobile",
          defaultValue: "Select a valid commission percentage.",
        })
      );
      return;
    }
    if (!accounts.some((account) => account.login === loginRaw)) {
      setFormError(
        t("copy_trade_login_required", {
          ns: "mobile",
          defaultValue: "Select a trading account.",
        })
      );
      return;
    }
    if (
      commissionPercent > 0 &&
      (!commissionLoginRaw ||
        !/^\d+$/.test(commissionLoginRaw) ||
        !accounts.some((account) => account.login === commissionLoginRaw))
    ) {
      setFormError(
        t("copy_trade_commission_account_required", {
          ns: "mobile",
          defaultValue: "Select a sharing account that should receive profit share.",
        })
      );
      return;
    }

    setSubmitting(true);
    try {
      const response = isEdit
        ? await updateCopyTradeMaster({
            LOGIN: Number(loginRaw),
            COMMISSION_PERCENT: commissionPercent,
            COMMISSION_LOGIN:
              commissionPercent > 0 ? Number(commissionLoginRaw) : Number(loginRaw),
          })
        : await becomeCopyTradeMaster({
            NAME: name,
            LOGIN: Number(loginRaw),
            COMMISSION_PERCENT: commissionPercent,
            COMMISSION_LOGIN:
              commissionPercent > 0 ? Number(commissionLoginRaw) : Number(loginRaw),
          });
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);

      if (code === AppConstants.responseOk || code === 200) {
        showSuccess(
          isEdit
            ? t("msub_edit_success", {
                ns: "mobile",
                defaultValue: "Master account updated successfully.",
              })
            : t("copy_trade_become_pending", {
                ns: "mobile",
                defaultValue:
                  "Your master application was submitted and is pending admin approval.",
              })
        );
        onSuccess?.(data?.DATA ?? data?.data ?? null);
        onClose?.();
        return;
      }

      if (!isEdit && (code === AppConstants.responseAlreadyOk || code === 205)) {
        showSuccess(
          t("copy_trade_already_master", {
            ns: "mobile",
            defaultValue:
              "This trading account already has a master application (pending or approved).",
          })
        );
        onSuccess?.();
        onClose?.();
        return;
      }

      const message =
        code === AppConstants.invalidInputs || code === 708
          ? t(isEdit ? "msub_edit_invalid" : "copy_trade_become_invalid", {
              ns: "mobile",
              defaultValue: isEdit
                ? "Could not update. Use one of your own trading account logins."
                : "Could not register. Use one of your own trading account logins.",
            })
          : t(isEdit ? "msub_edit_error" : "copy_trade_become_error", {
              ns: "mobile",
              defaultValue: isEdit
                ? "Unable to update master account. Please try again."
                : "Unable to become a master. Please try again.",
            });
      setFormError(message);
      showError(message);
    } catch (_) {
      const message = t(isEdit ? "msub_edit_error" : "copy_trade_become_error", {
        ns: "mobile",
        defaultValue: isEdit
          ? "Unable to update master account. Please try again."
          : "Unable to become a master. Please try again.",
      });
      setFormError(message);
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="copy-trade-modal-overlay"
      onClick={submitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="copy-trade-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="become-master-title"
      >
        <div className="copy-trade-modal-header">
          <h2 id="become-master-title" className="copy-trade-modal-title">
            {isEdit
              ? t("msub_edit_account", { ns: "mobile", defaultValue: "Edit Account" })
              : t("copy_trade_become_master", {
                  ns: "mobile",
                  defaultValue: "Become a Master",
                })}
          </h2>
          <button
            type="button"
            className="copy-trade-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label={t("close", { ns: "mobile", defaultValue: "Close" })}
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>

        <p className="copy-trade-modal-subtitle">
          {isEdit
            ? t("msub_edit_account_sub", {
                ns: "mobile",
                defaultValue:
                  "Update your commission rate and trading login. Your display name cannot be changed.",
              })
            : t("copy_trade_become_master_sub", {
                ns: "mobile",
                defaultValue:
                  "Set up your master account to start sharing trades and earning commissions from your followers.",
              })}
        </p>

        <form className="copy-trade-modal-form" onSubmit={handleSubmit}>
          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_full_name", { ns: "mobile", defaultValue: "Full Name" })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-regular fa-user copy-trade-input-icon" aria-hidden />
              <input
                type="text"
                className="copy-trade-input"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={submitting || isEdit}
                required={!isEdit}
              />
            </div>
          </label>

          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_account_number", { ns: "mobile", defaultValue: "Trading Account" })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-solid fa-hashtag copy-trade-input-icon" aria-hidden />
              <select
                className="copy-trade-input copy-trade-select"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                disabled={submitting || accountsLoading || accounts.length === 0}
                required
              >
                <option value="">
                  {accountsLoading
                    ? t("loading", { ns: "mobile", defaultValue: "Loading..." })
                    : accounts.length === 0
                      ? t("copy_trade_no_accounts", {
                          ns: "mobile",
                          defaultValue: "No trading accounts found",
                        })
                      : t("copy_trade_select_account", {
                          ns: "mobile",
                          defaultValue: "Select a trading account",
                        })}
                </option>
                {accounts.map((account) => (
                  <option key={account.login} value={account.login}>
                    {account.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_commissions", { ns: "mobile", defaultValue: "Profit Share (%)" })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-solid fa-percent copy-trade-input-icon" aria-hidden />
              <select
                className="copy-trade-input copy-trade-select"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                disabled={submitting}
                required
              >
                {COMMISSION_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value === 0
                      ? t("copy_trade_commission_disabled", {
                          ns: "mobile",
                          defaultValue: "Disabled",
                        })
                      : `${value}%`}
                  </option>
                ))}
              </select>
            </div>
            <span className="copy-trade-field-hint">
              {t("copy_trade_commission_hint", {
                ns: "mobile",
                defaultValue: "This is the percentage of profits you will keep from your copiers.",
              })}
            </span>
          </label>

          {Number(commission) > 0 ? (
            <label className="copy-trade-field">
              <span className="copy-trade-field-label">
                {t("copy_trade_commission_account", {
                  ns: "mobile",
                  defaultValue: "Sharing Account",
                })}
              </span>
              <div className="copy-trade-input-wrap">
                <i className="fa-solid fa-wallet copy-trade-input-icon" aria-hidden />
                <select
                  className="copy-trade-input copy-trade-select"
                  value={commissionAccount}
                  onChange={(e) => setCommissionAccount(e.target.value)}
                  disabled={submitting || accountsLoading || accounts.length === 0}
                  required
                >
                  <option value="">
                    {accountsLoading
                      ? t("loading", { ns: "mobile", defaultValue: "Loading..." })
                      : accounts.length === 0
                        ? t("copy_trade_no_accounts", {
                            ns: "mobile",
                            defaultValue: "No trading accounts found",
                          })
                        : t("copy_trade_select_commission_account", {
                            ns: "mobile",
                            defaultValue: "Select sharing account to receive profit share",
                          })}
                  </option>
                  {accounts.map((account) => (
                    <option key={`commission-${account.login}`} value={account.login}>
                      {account.label}
                    </option>
                  ))}
                </select>
              </div>
              <span className="copy-trade-field-hint">
                {t("copy_trade_commission_account_hint", {
                  ns: "mobile",
                  defaultValue:
                    "Profit share from copiers will be transferred to this sharing account.",
                })}
              </span>
            </label>
          ) : null}

          {formError ? <p className="copy-trade-form-error">{formError}</p> : null}

          {!isEdit ? (
            <div className="copy-trade-terms-note">
              <i className="fa-solid fa-circle-info" aria-hidden />
              <p>
                {t("copy_trade_terms_prefix", {
                  ns: "mobile",
                  defaultValue: "By becoming a Master, you agree to our",
                })}{" "}
                <button type="button" className="copy-trade-terms-link">
                  {t("copy_trade_master_terms", {
                    ns: "mobile",
                    defaultValue: "Master Trader Terms",
                  })}
                </button>{" "}
                {t("copy_trade_terms_suffix", {
                  ns: "mobile",
                  defaultValue: "and acknowledge that your performance data will be public.",
                })}
              </p>
            </div>
          ) : null}

          <div className="copy-trade-modal-actions">
            <button
              type="button"
              className="copy-trade-btn copy-trade-btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              {t("cancel", { ns: "mobile", defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className="copy-trade-btn copy-trade-btn--primary"
              disabled={
                submitting ||
                accountsLoading ||
                accounts.length === 0
              }
            >
              {submitting
                ? t("wait_a_moment", { ns: "mobile", defaultValue: "Please wait..." })
                : isEdit
                  ? t("msub_save_changes", {
                      ns: "mobile",
                      defaultValue: "Save Changes",
                    })
                  : t("copy_trade_become_master", {
                      ns: "mobile",
                      defaultValue: "Become a Master",
                    })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const getUserDisplayName = (user) => {
  if (!user) return "";
  const info =
    user.user_information ||
    user.userInformation ||
    user.USER_INFORMATION ||
    {};
  const fullFromInfo = String(
    info.FULL_NAME ?? info.fullName ?? info.full_name ?? ""
  ).trim();
  if (fullFromInfo) return fullFromInfo;

  const first = String(
    info.FIRST_NAME ??
      info.firstName ??
      info.first_name ??
      user.FIRST_NAME ??
      user.firstName ??
      user.first_name ??
      ""
  ).trim();
  const last = String(
    info.LAST_NAME ??
      info.lastName ??
      info.last_name ??
      user.LAST_NAME ??
      user.lastName ??
      user.last_name ??
      ""
  ).trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;

  return String(
    user.FULL_NAME ??
      user.fullName ??
      user.NAME ??
      user.name ??
      user.NICK_NAME ??
      user.nickName ??
      user.USER_NAME ??
      user.userName ??
      ""
  ).trim();
};

function CopySlaveModal({
  isOpen,
  onClose,
  onSuccess,
  master,
  user,
  t,
  mode = "create",
  initialValues = null,
}) {
  const isEdit = mode === "edit";
  const { success: showSuccess, error: showError } = useToast();
  const [fullName, setFullName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ratio, setRatio] = useState("100");
  const [lossLimit, setLossLimit] = useState("0");
  const [openCopyMode, setOpenCopyMode] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordAction, setPasswordAction] = useState("subscribe");
  const pendingSubscribeRef = useRef(null);
  const passwordActionRef = useRef("subscribe");

  useEffect(() => {
    if (!isOpen) {
      setFullName("");
      setAccountNumber("");
      setRatio("100");
      setLossLimit("0");
      setOpenCopyMode("");
      setAccounts([]);
      setAccountsLoading(false);
      setSubmitting(false);
      setFormError("");
      setShowPasswordPopup(false);
      setPasswordAction("subscribe");
      pendingSubscribeRef.current = null;
      passwordActionRef.current = "subscribe";
      return undefined;
    }

    // Prefer live session profile so the name is available even if prop is stale.
    let profileUser = user;
    try {
      const raw =
        sessionStorage.getItem("userData") || localStorage.getItem("userData");
      if (raw) profileUser = JSON.parse(raw);
    } catch (_) {
      /* keep prop */
    }
    setFullName(getUserDisplayName(profileUser) || "");

    const initialLogin = String(initialValues?.login ?? "").trim();
    const initialRatio = Number(initialValues?.ratio);
    const initialLossLimit = Number(initialValues?.lossLimit);
    setAccountNumber(initialLogin);
    setRatio(
      RATIO_OPTIONS.includes(initialRatio) ? String(initialRatio) : "100"
    );
    setLossLimit(
      LOSS_LIMIT_OPTIONS.includes(initialLossLimit)
        ? String(initialLossLimit)
        : "0"
    );
    const initialOpenCopyMode = Number(initialValues?.openCopyMode);
    setOpenCopyMode([1, 2].includes(initialOpenCopyMode) ? String(initialOpenCopyMode) : "");

    let cancelled = false;
    const loadAccounts = async () => {
      setAccountsLoading(true);
      try {
        const response = await getTradingAccounts(AccountType.REAL, 100, 1, null, {
          forceRefresh: true,
        });
        if (cancelled) return;
        const masterLogin = String(master?.tradingLogin ?? "").trim();
        const options = extractAccountsPayload(response)
          .map(mapAccountOption)
          .filter(
            (option) =>
              option &&
              option.type === AccountType.REAL &&
              option.login !== masterLogin
          );
        setAccounts(options);
        if (initialLogin && options.some((a) => a.login === initialLogin)) {
          setAccountNumber(initialLogin);
        } else if (!isEdit && options.length === 1) {
          setAccountNumber(options[0].login);
        }
      } catch (_) {
        if (!cancelled) {
          setAccounts([]);
          setFormError(
            t("copy_trade_accounts_load_error", {
              ns: "mobile",
              defaultValue: "Unable to load your trading accounts.",
            })
          );
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    };
    loadAccounts();

    return () => {
      cancelled = true;
    };
  }, [isOpen, t, user, master, isEdit, initialValues]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !showPasswordPopup) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, showPasswordPopup]);

  if (!isOpen || !master) return null;

  const busy = showPasswordPopup;
  const isDisconnectPassword = passwordAction === "disconnect";
  const startOptions = [
    {
      value: "1",
      title: t("copy_trade_start_wait", {
        ns: "mobile",
        defaultValue: "Wait until the master is flat",
      }),
      hint: t("copy_trade_start_wait_hint", {
        ns: "mobile",
        defaultValue:
          "Copying starts only after the master closes every open position. Those positions are not copied.",
      }),
    },
    {
      value: "2",
      title: t("copy_trade_start_market", {
        ns: "mobile",
        defaultValue: "Copy open positions at the current price",
      }),
      hint: t("copy_trade_start_market_hint", {
        ns: "mobile",
        defaultValue:
          "The master's open positions are opened on your account now at the live market price, then new trades are copied as usual.",
      }),
    },
  ];

  const openPasswordPopup = (action) => {
    passwordActionRef.current = action;
    setPasswordAction(action);
    setShowPasswordPopup(true);
  };

  const handleDisconnectClick = () => {
    openPasswordPopup("disconnect");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const name = fullName.trim();
    const loginRaw = String(accountNumber || "").trim();
    const ratioValue = Number(ratio);
    const lossLimitValue = Number(lossLimit);
    const masterLogin = Number(master.tradingLogin);

    if (!name) {
      setFormError(
        t("copy_trade_name_required", {
          ns: "mobile",
          defaultValue: "Full name is required.",
        })
      );
      return;
    }
    if (!loginRaw || !/^\d+$/.test(loginRaw) || !accounts.some((a) => a.login === loginRaw)) {
      setFormError(
        t("copy_trade_login_required", {
          ns: "mobile",
          defaultValue: "Select a trading account.",
        })
      );
      return;
    }
    if (!RATIO_OPTIONS.includes(ratioValue)) {
      setFormError(
        t("copy_trade_ratio_invalid", {
          ns: "mobile",
          defaultValue: "Select a valid ratio.",
        })
      );
      return;
    }
    if (!LOSS_LIMIT_OPTIONS.includes(lossLimitValue)) {
      setFormError(
        t("copy_trade_loss_limit_invalid", {
          ns: "mobile",
          defaultValue: "Select a valid loss limit.",
        })
      );
      return;
    }
    const openCopyModeValue = Number(openCopyMode);
    if (![1, 2].includes(openCopyModeValue)) {
      setFormError(
        t("copy_trade_start_required", {
          ns: "mobile",
          defaultValue: "Choose how to handle the master's open positions.",
        })
      );
      return;
    }
    if (!masterLogin) {
      setFormError(
        t("copy_trade_subscribe_error", {
          ns: "mobile",
          defaultValue: "Unable to copy this master. Please try again.",
        })
      );
      return;
    }

    pendingSubscribeRef.current = {
      MASTER_LOGIN: masterLogin,
      LOGIN: Number(loginRaw),
      NAME: name,
      RATIO: ratioValue,
      LOSS_LIMIT_PERCENT: lossLimitValue,
      OPEN_COPY_MODE: openCopyModeValue,
    };
    openPasswordPopup("subscribe");
  };

  const handleCopyPasswordApply = async (plainPassword) => {
    if (passwordActionRef.current === "disconnect") {
      const masterLogin = Number(master?.tradingLogin);
      if (!masterLogin) {
        throw new Error("Missing master login");
      }
      return unsubscribeCopyTradeMaster({
        MASTER_LOGIN: masterLogin,
        PASSWORD: plainPassword,
      });
    }

    const pending = pendingSubscribeRef.current;
    if (!pending) {
      throw new Error("Missing copy payload");
    }
    return subscribeCopyTradeMaster({
      ...pending,
      PASSWORD: plainPassword,
    });
  };

  const handlePasswordDialogClose = (result, data) => {
    const action = passwordActionRef.current;
    setShowPasswordPopup(false);
    const responseData = data?.responseData || {};
    const code = Number(responseData.CODE ?? responseData.code ?? 0);

    if (result === "ok") {
      if (action === "disconnect") {
        showSuccess(
          t("copy_trade_unsubscribe_success", {
            ns: "mobile",
            defaultValue: "Disconnected from {{name}}.",
            name: master.name,
          })
        );
      } else {
        showSuccess(
          isEdit
            ? t("copy_trade_edit_success", {
                ns: "mobile",
                defaultValue: "Copy settings updated successfully.",
              })
            : code === AppConstants.responseAlreadyOk || code === 205
              ? t("copy_trade_subscribe_already", {
                  ns: "mobile",
                  defaultValue: "You are already copying this master. Settings were updated.",
                })
              : t("copy_trade_subscribe_success", {
                  ns: "mobile",
                  defaultValue: "You are now copying this master.",
                })
        );
      }
      pendingSubscribeRef.current = null;
      onSuccess?.();
      onClose?.();
      return;
    }

    if (result === "canceled") {
      return;
    }

    const message =
      action === "disconnect"
        ? t("copy_trade_unsubscribe_error", {
            ns: "mobile",
            defaultValue: "Unable to disconnect. Please try again.",
          })
        : code === AppConstants.invalidInputs || code === 708
          ? t("copy_trade_subscribe_invalid", {
              ns: "mobile",
              defaultValue: "Could not copy. Use one of your own real trading accounts.",
            })
          : isEdit
            ? t("copy_trade_edit_error", {
                ns: "mobile",
                defaultValue: "Unable to update copy settings. Please try again.",
              })
            : t("copy_trade_subscribe_error", {
                ns: "mobile",
                defaultValue: "Unable to copy this master. Please try again.",
              });
    setFormError(message);
    showError(message);
  };

  return (
    <>
    <div
      className="copy-trade-modal-overlay"
      onClick={busy ? undefined : onClose}
      role="presentation"
    >
      <div
        className="copy-trade-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-slave-title"
      >
        <div className="copy-trade-modal-header">
          <h2 id="copy-slave-title" className="copy-trade-modal-title">
            {isEdit
              ? t("copy_trade_edit_settings", {
                  ns: "mobile",
                  defaultValue: "Edit Copy Settings",
                })
              : t("copy_trade_copy_master", {
                  ns: "mobile",
                  defaultValue: "Copy Master",
                })}
          </h2>
          <button
            type="button"
            className="copy-trade-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label={t("close", { ns: "mobile", defaultValue: "Close" })}
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>

        <p className="copy-trade-modal-subtitle">
          {isEdit
            ? t("copy_trade_edit_settings_sub", {
                ns: "mobile",
                defaultValue:
                  "Update the trading account, ratio, or loss limit for copying {{name}}.",
                name: master.name,
              })
            : t("copy_trade_copy_master_sub", {
                ns: "mobile",
                defaultValue:
                  "Create a slave account to automatically copy trades from {{name}}.",
                name: master.name,
              })}
        </p>

        <form className="copy-trade-modal-form" onSubmit={handleSubmit}>
          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_full_name", { ns: "mobile", defaultValue: "Full Name" })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-regular fa-user copy-trade-input-icon" aria-hidden />
              <input
                type="text"
                className="copy-trade-input"
                value={fullName}
                disabled
                readOnly
              />
            </div>
          </label>

          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_account_number", {
                ns: "mobile",
                defaultValue: "Trading Account",
              })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-solid fa-hashtag copy-trade-input-icon" aria-hidden />
              <select
                className="copy-trade-input copy-trade-select"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                disabled={submitting || accountsLoading || accounts.length === 0}
                required
              >
                <option value="">
                  {accountsLoading
                    ? t("loading", { ns: "mobile", defaultValue: "Loading..." })
                    : accounts.length === 0
                      ? t("copy_trade_no_accounts", {
                          ns: "mobile",
                          defaultValue: "No trading accounts found",
                        })
                      : t("copy_trade_select_account", {
                          ns: "mobile",
                          defaultValue: "Select a trading account",
                        })}
                </option>
                {accounts.map((account) => (
                  <option key={account.login} value={account.login}>
                    {account.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_ratio", { ns: "mobile", defaultValue: "Ratio (%)" })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-solid fa-scale-balanced copy-trade-input-icon" aria-hidden />
              <select
                className="copy-trade-input copy-trade-select"
                value={ratio}
                onChange={(e) => setRatio(e.target.value)}
                disabled={submitting}
                required
              >
                {RATIO_OPTIONS.map((value) => (
                  <option key={value} value={String(value)}>
                    {value}%
                  </option>
                ))}
              </select>
            </div>
            <span className="copy-trade-field-hint">
              {t("copy_trade_ratio_hint", {
                ns: "mobile",
                defaultValue: "Volume ratio used when copying the master’s trades.",
              })}
            </span>
          </label>

          <label className="copy-trade-field">
            <span className="copy-trade-field-label">
              {t("copy_trade_loss_limit", {
                ns: "mobile",
                defaultValue: "Loss Limit (%)",
              })}
            </span>
            <div className="copy-trade-input-wrap">
              <i className="fa-solid fa-shield-halved copy-trade-input-icon" aria-hidden />
              <select
                className="copy-trade-input copy-trade-select"
                value={lossLimit}
                onChange={(e) => setLossLimit(e.target.value)}
                disabled={submitting}
                required
              >
                {LOSS_LIMIT_OPTIONS.map((value) => (
                  <option key={value} value={String(value)}>
                    {value === 0
                      ? t("copy_trade_loss_limit_disabled", {
                          ns: "mobile",
                          defaultValue: "Disabled",
                        })
                      : `${value}%`}
                  </option>
                ))}
              </select>
            </div>
            <span className="copy-trade-field-hint">
              {t("copy_trade_loss_limit_hint", {
                ns: "mobile",
                defaultValue:
                  "Stop copying if account equity drops by this percent from the start.",
              })}
            </span>
          </label>

          <fieldset className="copy-trade-field copy-trade-start-options">
            <legend className="copy-trade-field-label">
              {t("copy_trade_start_label", {
                ns: "mobile",
                defaultValue: "When to start copying",
              })}
            </legend>
            {startOptions.map((option) => {
              const selected = openCopyMode === option.value;
              return (
                <label
                  key={option.value}
                  className={`copy-trade-start-option${
                    selected ? " is-selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="openCopyMode"
                    value={option.value}
                    checked={selected}
                    onChange={() => setOpenCopyMode(option.value)}
                    disabled={submitting}
                  />
                  <span>
                    <span className="copy-trade-start-option-title">
                      {option.title}
                    </span>
                    <span className="copy-trade-start-option-hint">
                      {option.hint}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {formError ? <p className="copy-trade-form-error">{formError}</p> : null}

          {isEdit ? (
            <button
              type="button"
              className="copy-trade-btn copy-trade-btn--disconnect copy-trade-btn--disconnect-full"
              onClick={handleDisconnectClick}
              disabled={busy}
            >
              {t("copy_trade_disconnect", {
                ns: "mobile",
                defaultValue: "Disconnect",
              })}
            </button>
          ) : null}

          <div className="copy-trade-modal-actions">
            <button
              type="button"
              className="copy-trade-btn copy-trade-btn--ghost"
              onClick={onClose}
              disabled={busy}
            >
              {t("cancel", { ns: "mobile", defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className="copy-trade-btn copy-trade-btn--primary"
              disabled={busy || accountsLoading || accounts.length === 0}
            >
              {submitting
                ? t("wait_a_moment", { ns: "mobile", defaultValue: "Please wait..." })
                : isEdit
                  ? t("msub_save_changes", {
                      ns: "mobile",
                      defaultValue: "Save Changes",
                    })
                  : t("copy_trade_copy", { ns: "mobile", defaultValue: "Copy" })}
            </button>
          </div>
        </form>
      </div>
    </div>

    <TransactionPasswordDialog
      open={showPasswordPopup}
      onClose={handlePasswordDialogClose}
      applyFn={handleCopyPasswordApply}
      title={
        isDisconnectPassword
          ? t("copy_trade_confirm_disconnect_title", {
              ns: "mobile",
              defaultValue: "Confirm disconnect",
            })
          : t("copy_trade_confirm_title", {
              ns: "mobile",
              defaultValue: "Confirm copy",
            })
      }
      firstMessage={
        isDisconnectPassword
          ? t("copy_trade_confirm_disconnect_message", {
              ns: "mobile",
              defaultValue:
                "You will stop copying trades from {{name}}. Confirm with your password to proceed.",
              name: master.name,
            })
          : isEdit
            ? t("copy_trade_confirm_edit_message", {
                ns: "mobile",
                defaultValue:
                  "You will update copy settings for {{name}}. Confirm with your password to proceed.",
                name: master.name,
              })
            : t("copy_trade_confirm_message", {
                ns: "mobile",
                defaultValue:
                  "You will start copying trades from {{name}}. Confirm with your password to proceed.",
                name: master.name,
              })
      }
    />
    </>
  );
}

export default function CopyTrade() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeFilter, setActiveFilter] = useState(FILTER_TOP_ROI);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isBecomeMasterOpen, setIsBecomeMasterOpen] = useState(false);
  const [selectedMasterToCopy, setSelectedMasterToCopy] = useState(null);
  const [copyModalMode, setCopyModalMode] = useState("create"); // create | edit
  const [masterStatus, setMasterStatus] = useState(null); // null (loading) | none | pending | approved
  const [myMasterLogin, setMyMasterLogin] = useState("");
  const [subscribedMasterLogins, setSubscribedMasterLogins] = useState(() => new Set());
  const [subscriptionsByMaster, setSubscriptionsByMaster] = useState(() => new Map());
  const [liveSymbols, setLiveSymbols] = useState([]);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (user && isCopyTradeDisabled(user)) {
      navigate("/pages/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const hideBecomeMaster =
    !user || (masterStatus === "none" && isBecomeMasterDisabled(user));

  useEffect(() => {
    let cancelled = false;
    let fallbackTimerId = null;
    let wsConnected = false;

    const applyRows = (rows) => {
      if (cancelled) return;
      const mapped = (Array.isArray(rows) ? rows : [])
        .map(mapQuoteFromApi)
        .filter(Boolean);
      if (mapped.length > 0) {
        setLiveSymbols(mapped);
      }
    };

    const loadQuotesHttp = async () => {
      try {
        const response = await getCopyTradeSymbolQuotes();
        if (cancelled) return;
        const data = response?.decryptedData || response?.data || {};
        const code = Number(data?.CODE ?? data?.code ?? 0);
        if (code && code !== AppConstants.responseOk && code !== 200) {
          return;
        }
        applyRows(extractQuotesPayload(response));
      } catch (_) {
        /* keep last good quotes */
      }
    };

    const stopFallbackPoll = () => {
      if (fallbackTimerId) {
        window.clearInterval(fallbackTimerId);
        fallbackTimerId = null;
      }
    };

    const startFallbackPoll = () => {
      if (fallbackTimerId || cancelled) return;
      fallbackTimerId = window.setInterval(loadQuotesHttp, SYMBOL_QUOTES_FALLBACK_POLL_MS);
    };

    // Immediate paint from cache/HTTP, then prefer WebSocket fan-out.
    loadQuotesHttp();

    const detachPusher = attachCopyTradeQuotesPusher((rows) => {
      applyRows(rows);
      if (Array.isArray(rows) && rows.length > 0) {
        wsConnected = true;
        stopFallbackPoll();
      }
    }, {
      onDisconnected: () => {
        wsConnected = false;
        startFallbackPoll();
      },
    });

    // If WS never connects, keep a slow HTTP fallback.
    const connectWatchdog = window.setTimeout(() => {
      if (!cancelled && !wsConnected) {
        startFallbackPoll();
      }
    }, 4000);

    return () => {
      cancelled = true;
      window.clearTimeout(connectWatchdog);
      stopFallbackPoll();
      detachPusher();
    };
  }, []);

  const fetchMasterStatus = useCallback(async () => {
    try {
      const response = await getCopyTradeMasterStatus();
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        setMasterStatus("none");
        setMyMasterLogin("");
        return;
      }
      const payload = data?.DATA ?? data?.data ?? {};
      const status = String(payload?.STATUS ?? payload?.status ?? "none").toLowerCase();
      setMasterStatus(
        status === "pending" || status === "approved" ? status : "none"
      );
      const master = payload?.MASTER ?? payload?.master ?? null;
      const login = String(master?.LOGIN ?? master?.login ?? "").trim();
      setMyMasterLogin(status === "approved" && login ? login : "");
    } catch (_) {
      setMasterStatus("none");
      setMyMasterLogin("");
    }
  }, []);

  const fetchMySubscriptions = useCallback(async () => {
    try {
      const response = await getCopyTradeMySubscriptions();
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        setSubscribedMasterLogins(new Set());
        setSubscriptionsByMaster(new Map());
        return;
      }
      const payload = data?.DATA ?? data?.data ?? {};
      const logins = Array.isArray(payload?.MASTER_LOGINS)
        ? payload.MASTER_LOGINS
        : Array.isArray(payload?.masterLogins)
          ? payload.masterLogins
          : [];
      const items = Array.isArray(payload?.ITEMS)
        ? payload.ITEMS
        : Array.isArray(payload?.items)
          ? payload.items
          : [];
      const byMaster = new Map();
      items.forEach((item) => {
        const masterLogin = String(
          item?.MASTER_LOGIN ?? item?.masterLogin ?? ""
        ).trim();
        if (!masterLogin) return;
        byMaster.set(masterLogin, {
          login: String(item?.SLAVE_LOGIN ?? item?.slaveLogin ?? "").trim(),
          ratio: Number(item?.RATIO ?? item?.ratio ?? 100),
          lossLimit: Number(
            item?.LOSS_LIMIT_PERCENT ?? item?.lossLimitPercent ?? 0
          ),
          openCopyMode: Number(
            item?.OPEN_COPY_MODE ?? item?.openCopyMode ?? 0
          ),
          copyArmed: Number(item?.COPY_ARMED ?? item?.copyArmed ?? 1),
          existingCopied: Number(
            item?.EXISTING_COPIED ?? item?.existingCopied ?? 1
          ),
        });
      });
      setSubscriptionsByMaster(byMaster);
      setSubscribedMasterLogins(
        new Set(
          [
            ...logins.map((login) => String(login).trim()),
            ...byMaster.keys(),
          ].filter(Boolean)
        )
      );
    } catch (_) {
      setSubscribedMasterLogins(new Set());
      setSubscriptionsByMaster(new Map());
    }
  }, []);

  const fetchMasters = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await getCopyTradeMasters({});
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        setMasters([]);
        setLoadError(
          t("copy_trade_load_error", {
            ns: "mobile",
            defaultValue: "Unable to load masters. Please try again.",
          })
        );
        return;
      }
      setMasters(extractMastersPayload(response).map(mapMasterFromApi));
    } catch (_) {
      setMasters([]);
      setLoadError(
        t("copy_trade_load_error", {
          ns: "mobile",
          defaultValue: "Unable to load masters. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchMasters();
    fetchMasterStatus();
    fetchMySubscriptions();
  }, [fetchMasters, fetchMasterStatus, fetchMySubscriptions]);

  useEffect(() => {
    const copyMaster = location.state?.copyMaster;
    if (!copyMaster) return;
    setCopyModalMode("create");
    setSelectedMasterToCopy(copyMaster);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, appliedSearch]);

  const filteredMasters = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase();
    let list = masters.filter((master) => {
      if (!query) return true;
      return (
        master.name.toLowerCase().includes(query) ||
        master.tradingLogin.includes(query)
      );
    });

    if (activeFilter === FILTER_TOP_EQUITY) {
      list = [...list].sort((a, b) => b.equity - a.equity);
    } else if (activeFilter === FILTER_TOP_ROI) {
      list = [...list].sort((a, b) => b.totalGrowth - a.totalGrowth || b.equity - a.equity);
    } else if (activeFilter === FILTER_LOW_RISK) {
      list = [...list].sort((a, b) => a.commission - b.commission || b.equity - a.equity);
    }

    return list;
  }, [masters, activeFilter, appliedSearch]);

  const totalItems = filteredMasters.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PER_PAGE;
  const pageMasters = filteredMasters.slice(pageStart, pageStart + PER_PAGE);
  const showingFrom = totalItems === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PER_PAGE, totalItems);

  const filters = [
    {
      id: FILTER_TOP_ROI,
      label: t("copy_trade_filter_top_roi", { ns: "mobile", defaultValue: "Top ROI" }),
    },
    {
      id: FILTER_TOP_EQUITY,
      label: t("copy_trade_filter_top_equity", { ns: "mobile", defaultValue: "Top Equity" }),
    },
    {
      id: FILTER_LOW_RISK,
      label: t("copy_trade_filter_low_risk", { ns: "mobile", defaultValue: "Low Risk" }),
    },
  ];

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: t("copy_trade_col_master_name", {
          ns: "mobile",
          defaultValue: "MASTER NAME",
        }),
        className: "copy-trade-col-master",
        render: (row) => (
          <div className="copy-trade-master-cell">
            <span
              className="copy-trade-avatar"
              style={{ backgroundColor: row.avatarColor }}
            >
              {row.initials}
            </span>
            <div className="copy-trade-master-meta">
              <div className="copy-trade-master-name-row">
                <span className="copy-trade-master-name">{row.name}</span>
                {row.verified && (
                  <i
                    className="fa-solid fa-circle-check copy-trade-verified"
                    title="Verified"
                    aria-label="Verified"
                  />
                )}
                {row.api && <span className="copy-trade-api-badge">API</span>}
              </div>
              <span className="copy-trade-copiers">
                {formatCopiers(row.copiers)}{" "}
                {t("copy_trade_copiers", { ns: "mobile", defaultValue: "Copiers" })}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: "tradingLogin",
        label: t("copy_trade_col_trading_login", {
          ns: "mobile",
          defaultValue: "TRADING LOGIN",
        }),
        className: "copy-trade-col-login",
        render: (row) => <span className="ltr-content">{row.tradingLogin}</span>,
      },
      {
        key: "equity",
        label: t("copy_trade_col_equity", {
          ns: "mobile",
          defaultValue: "EQUITY",
        }),
        className: "copy-trade-col-equity",
        render: (row) => <span className="ltr-content">{formatEquity(row.equity)}</span>,
      },
      {
        key: "weeklyGrowth",
        label: t("copy_trade_col_weekly_growth", {
          ns: "mobile",
          defaultValue: "WEEKLY GROWTH",
        }),
        className: "copy-trade-col-growth",
        render: (row) => (
          <span className={`${growthClass(row.weeklyGrowth)} ltr-content`}>
            {formatGrowth(row.weeklyGrowth)}
          </span>
        ),
      },
      {
        key: "totalGrowth",
        label: t("copy_trade_col_total_growth", {
          ns: "mobile",
          defaultValue: "TOTAL GROWTH",
        }),
        className: "copy-trade-col-growth",
        render: (row) => (
          <span className={`${growthClass(row.totalGrowth)} ltr-content`}>
            {formatGrowth(row.totalGrowth)}
          </span>
        ),
      },
      {
        key: "commission",
        label: t("copy_trade_col_commission", {
          ns: "mobile",
          defaultValue: "COMMISSION",
        }),
        className: "copy-trade-col-commission",
        render: (row) => `${row.commission}%`,
      },
      {
        key: "action",
        label: t("copy_trade_col_action", {
          ns: "mobile",
          defaultValue: "ACTION",
        }),
        className: "copy-trade-col-action action-column",
        render: (row) => {
          const login = String(row.tradingLogin || "").trim();
          const isOwnMaster =
            Boolean(myMasterLogin) && login === String(myMasterLogin).trim();
          const isSubscribed = subscribedMasterLogins.has(login);
          const viewButton = (
            <button
              type="button"
              className="copy-trade-view-btn"
              aria-label={t("copy_trade_view_strategy", {
                ns: "mobile",
                defaultValue: "View strategy",
              })}
              title={t("copy_trade_view_strategy", {
                ns: "mobile",
                defaultValue: "View strategy",
              })}
              onClick={() =>
                navigate(`/pages/copy-trade/view/${encodeURIComponent(login || row.id)}`, {
                  state: { master: row },
                })
              }
            >
              <i className="fa-solid fa-eye" aria-hidden />
            </button>
          );

          let actionControl = null;
          if (isOwnMaster) {
            actionControl = (
              <button
                type="button"
                className="copy-trade-action-btn copy-trade-action-btn--full"
                disabled
                title={t("copy_trade_own_master", {
                  ns: "mobile",
                  defaultValue: "This is your master account",
                })}
              >
                {t("copy_trade_you", { ns: "mobile", defaultValue: "You" })}
              </button>
            );
          } else if (isSubscribed) {
            actionControl = (
              <button
                type="button"
                className="copy-trade-edit-btn"
                aria-label={t("copy_trade_edit_settings", {
                  ns: "mobile",
                  defaultValue: "Edit Copy Settings",
                })}
                title={t("copy_trade_edit_settings", {
                  ns: "mobile",
                  defaultValue: "Edit Copy Settings",
                })}
                onClick={() => {
                  setCopyModalMode("edit");
                  setSelectedMasterToCopy(row);
                }}
              >
                <i className="fa-solid fa-pencil" aria-hidden />
              </button>
            );
          } else if (row.slotsFull) {
            actionControl = (
              <button type="button" className="copy-trade-action-btn copy-trade-action-btn--full" disabled>
                {t("copy_trade_full", { ns: "mobile", defaultValue: "Full" })}
              </button>
            );
          } else {
            actionControl = (
              <button
                type="button"
                className="copy-trade-action-btn"
                onClick={() => {
                  setCopyModalMode("create");
                  setSelectedMasterToCopy(row);
                }}
              >
                {t("copy_trade_copy", { ns: "mobile", defaultValue: "Copy" })}
              </button>
            );
          }

          return (
            <div className="copy-trade-action-cell">
              {viewButton}
              {actionControl}
            </div>
          );
        },
      },
    ],
    [t, myMasterLogin, subscribedMasterLogins, navigate]
  );

  const paginationSummary = (
    <span className="ib-clients-pagination-summary">
      {t("copy_trade_showing", {
        ns: "mobile",
        defaultValue: "Showing {{from}} to {{to}} of {{total}} Masters",
        from: showingFrom,
        to: showingTo,
        total: totalItems,
      })}
    </span>
  );

  const noDataMessage = t("copy_trade_no_masters", {
    ns: "mobile",
    defaultValue: "No masters found.",
  });

  return (
    <div className="copy-trade-page">
      <Sidebar user={user} />
      <div className="copy-trade-content accounts-list-content">
        <div className="copy-trade-header">
          <div className="copy-trade-header-text">
            <h1 className="copy-trade-title">
              {t("copy_trade", { ns: "mobile", defaultValue: "Copy Trading" })}
            </h1>
            <p className="copy-trade-subtitle">
              {t("copy_trade_subtitle", {
                ns: "mobile",
                defaultValue: "Automatically copy the trades of top-performing master traders.",
              })}
            </p>
          </div>
          <div className="copy-trade-header-actions">
            {masterStatus == null ? (
              <span
                className="accounts-skeleton-bar copy-trade-btn-skeleton"
                aria-hidden
              />
            ) : (
              <>
                {masterStatus === "approved" && (
                  <button
                    type="button"
                    className="copy-trade-btn copy-trade-btn--ghost"
                    onClick={() => navigate("/pages/copy-trade/my-subscriptions")}
                  >
                    {t("copy_trade_my_subscriptions", {
                      ns: "mobile",
                      defaultValue: "My Subscriptions",
                    })}
                  </button>
                )}
                {!hideBecomeMaster && (
                <button
                  type="button"
                  className="copy-trade-btn copy-trade-btn--primary"
                  disabled={masterStatus === "pending" || masterStatus === "approved"}
                  onClick={() => {
                    if (masterStatus === "none" && !isBecomeMasterDisabled(user)) {
                      setIsBecomeMasterOpen(true);
                    }
                  }}
                >
                  {masterStatus === "pending"
                    ? t("copy_trade_become_pending_btn", {
                        ns: "mobile",
                        defaultValue: "Pending Approval",
                      })
                    : masterStatus === "approved"
                      ? t("copy_trade_already_master_btn", {
                          ns: "mobile",
                          defaultValue: "You are a Master",
                        })
                      : t("copy_trade_become_master", {
                          ns: "mobile",
                          defaultValue: "Become a Master",
                        })}
                </button>
                )}
              </>
            )}
          </div>
        </div>

        <section className="copy-trade-symbols" aria-label="Live top symbols">
          <div className="copy-trade-symbols-label">
            <i className="fa-solid fa-bolt" aria-hidden />
            <span>
              {t("copy_trade_live_top_symbols", {
                ns: "mobile",
                defaultValue: "LIVE SYMBOLS",
              })}
            </span>
          </div>
          <div className="copy-trade-symbols-track">
            {liveSymbols.length === 0 ? (
              Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={`live-symbol-skeleton-${index}`}
                  className="copy-trade-symbol-card copy-trade-symbol-card--skeleton"
                  aria-hidden={index > 0}
                  aria-label={
                    index === 0
                      ? t("copy_trade_live_symbols_loading", {
                          ns: "mobile",
                          defaultValue: "Loading prices...",
                        })
                      : undefined
                  }
                >
                  <span className="accounts-skeleton-bar copy-trade-symbol-skeleton copy-trade-symbol-skeleton--name" />
                  <span className="accounts-skeleton-bar copy-trade-symbol-skeleton copy-trade-symbol-skeleton--price" />
                  <span className="accounts-skeleton-bar copy-trade-symbol-skeleton copy-trade-symbol-skeleton--change" />
                </div>
              ))
            ) : (
              liveSymbols.map((item) => {
                const isUp = item.change >= 0;
                return (
                  <div key={item.symbol} className="copy-trade-symbol-card">
                    <span className="copy-trade-symbol-name">{item.display}</span>
                    <span className={`copy-trade-symbol-price ${isUp ? "is-up" : "is-down"}`}>
                      {item.price}
                    </span>
                    <span className={`copy-trade-symbol-change ${isUp ? "is-up" : "is-down"}`}>
                      {isUp ? "+" : ""}
                      {item.change.toFixed(1)}%
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <div className="account-type-switcher-wrapper copy-trade-filters-row">
          <div className="account-type-switcher" role="tablist">
            {filters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={activeFilter === filter.id}
                className={`switcher-btn${activeFilter === filter.id ? " active" : ""}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <DataTable
          title={t("copy_trade_masters_title", {
            ns: "mobile",
            defaultValue: "Masters",
          })}
          subtitle={`(${showingFrom}-${showingTo}/${totalItems})`}
          columns={columns}
          data={pageMasters}
          rowKey={(row) => row.id}
          currentPage={safePage}
          totalItems={totalItems}
          perPage={PER_PAGE}
          onPageChange={setCurrentPage}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSearch={() => setAppliedSearch(searchTerm)}
          onClearSearch={() => {
            setSearchTerm("");
            setAppliedSearch("");
          }}
          searchPlaceholder={t("copy_trade_search_placeholder", {
            ns: "mobile",
            defaultValue: "Search Master...",
          })}
          loading={loading}
          loadingMessage={t("loading", { ns: "mobile", defaultValue: "Loading..." })}
          error={loadError || undefined}
          noDataMessage={noDataMessage}
          containerClassName="container-fluid px-0 w-100 copy-trade-table-container"
          tableClassName="copy-trade-masters-table"
          paginationLeading={paginationSummary}
        />
      </div>

      <BecomeMasterModal
        isOpen={isBecomeMasterOpen}
        onClose={() => setIsBecomeMasterOpen(false)}
        onSuccess={() => {
          setMasterStatus("pending");
          fetchMasterStatus();
          fetchMasters();
        }}
        t={t}
      />

      <CopySlaveModal
        isOpen={Boolean(selectedMasterToCopy)}
        onClose={() => {
          setSelectedMasterToCopy(null);
          setCopyModalMode("create");
        }}
        onSuccess={() => {
          fetchMasters();
          fetchMySubscriptions();
        }}
        master={selectedMasterToCopy}
        user={user}
        t={t}
        mode={copyModalMode}
        initialValues={
          selectedMasterToCopy
            ? subscriptionsByMaster.get(
                String(selectedMasterToCopy.tradingLogin || "").trim()
              ) || null
            : null
        }
      />
    </div>
  );
}
